/**
 * AddRFIModal Component
 * Form for creating and editing RFIs.
 * Follows AddPermitModal pattern (Gold Standard).
 *
 * @see docs/patterns/frontend/REACT_QUERY_PATTERNS.md
 */
import { useState, useEffect } from 'react';
import { RFI, RFICreate, IMPACT_LEVELS, IMPACT_LEVEL_LABELS, ImpactLevel } from '@/types/rfis';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateRFI, useUpdateRFI } from '@/hooks';
import { isValidEmail } from '@/lib/email-validation';

interface AddRFIModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  editingRFI?: RFI | null;
}

const EMPTY_FORM: Partial<RFICreate> = {
  subject: '',
  question: '',
  background: '',
  drawing_reference: '',
  spec_reference: '',
  cost_impact: null,
  schedule_impact: null,
  assigned_to: '',
  assigned_to_email: '',
  date_due: '',
};

export function AddRFIModal({ isOpen, onClose, projectId, editingRFI }: AddRFIModalProps) {
  const [formData, setFormData] = useState<Partial<RFICreate>>(EMPTY_FORM);

  const createRFI = useCreateRFI();
  const updateRFI = useUpdateRFI();
  const isSaving = createRFI.isPending || updateRFI.isPending;

  // Populate form when editing
  useEffect(() => {
    if (editingRFI) {
      setFormData({
        subject: editingRFI.subject ?? '',
        question: editingRFI.question ?? '',
        background: editingRFI.background ?? '',
        drawing_reference: editingRFI.drawing_reference ?? '',
        spec_reference: editingRFI.spec_reference ?? '',
        cost_impact: editingRFI.cost_impact ?? null,
        schedule_impact: editingRFI.schedule_impact ?? null,
        assigned_to: editingRFI.assigned_to ?? '',
        assigned_to_email: editingRFI.assigned_to_email ?? '',
        date_due: editingRFI.date_due ?? '',
      });
    } else {
      setFormData(EMPTY_FORM);
    }
  }, [editingRFI, isOpen]);

  const handleChange = (field: keyof RFICreate, value: string | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const trimmedEmail = formData.assigned_to_email?.trim() ?? '';
  const emailError =
    trimmedEmail.length > 0 && !isValidEmail(trimmedEmail)
      ? 'Enter a valid email address.'
      : '';

  const handleSubmit = async () => {
    if (!formData.subject?.trim()) return;
    if (emailError) return;

    const payload: RFICreate = {
      subject: formData.subject!.trim(),
      question: formData.question || null,
      background: formData.background || null,
      drawing_reference: formData.drawing_reference || null,
      spec_reference: formData.spec_reference || null,
      cost_impact: formData.cost_impact || null,
      schedule_impact: formData.schedule_impact || null,
      assigned_to: formData.assigned_to || null,
      assigned_to_email: formData.assigned_to_email || null,
      date_due: formData.date_due || null,
    };

    if (editingRFI) {
      await updateRFI.mutateAsync({ id: editingRFI.id, data: payload });
    } else {
      await createRFI.mutateAsync({ projectId, rfi: payload });
    }

    onClose();
  };

  const isEditing = Boolean(editingRFI);
  const canSave = Boolean(formData.subject?.trim()) && !emailError && !isSaving;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit RFI' : 'Add RFI'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Subject (required) */}
          <div className="space-y-1">
            <Label htmlFor="rfi-subject">Subject *</Label>
            <Input
              id="rfi-subject"
              placeholder="Brief description of the question"
              value={formData.subject ?? ''}
              onChange={(e) => handleChange('subject', e.target.value)}
            />
          </div>

          {/* Question */}
          <div className="space-y-1">
            <Label htmlFor="rfi-question">Question</Label>
            <Textarea
              id="rfi-question"
              placeholder="Detailed question or clarification needed..."
              rows={4}
              value={formData.question ?? ''}
              onChange={(e) => handleChange('question', e.target.value)}
            />
          </div>

          {/* Background */}
          <div className="space-y-1">
            <Label htmlFor="rfi-background">Background</Label>
            <Textarea
              id="rfi-background"
              placeholder="Relevant context or background information..."
              rows={3}
              value={formData.background ?? ''}
              onChange={(e) => handleChange('background', e.target.value)}
            />
          </div>

          {/* References */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="rfi-drawing-ref">Drawing Reference</Label>
              <Input
                id="rfi-drawing-ref"
                placeholder="e.g. A1.01"
                value={formData.drawing_reference ?? ''}
                onChange={(e) => handleChange('drawing_reference', e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="rfi-spec-ref">Spec Reference</Label>
              <Input
                id="rfi-spec-ref"
                placeholder="e.g. Section 03300"
                value={formData.spec_reference ?? ''}
                onChange={(e) => handleChange('spec_reference', e.target.value)}
              />
            </div>
          </div>

          {/* Impact */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Cost Impact</Label>
              <Select
                value={formData.cost_impact ?? ''}
                onValueChange={(v) => handleChange('cost_impact', v as ImpactLevel || null)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {IMPACT_LEVELS.map((level) => (
                    <SelectItem key={level} value={level}>
                      {IMPACT_LEVEL_LABELS[level]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Schedule Impact</Label>
              <Select
                value={formData.schedule_impact ?? ''}
                onValueChange={(v) => handleChange('schedule_impact', v as ImpactLevel || null)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {IMPACT_LEVELS.map((level) => (
                    <SelectItem key={level} value={level}>
                      {IMPACT_LEVEL_LABELS[level]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Assignment */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="rfi-assigned-to">Assigned To</Label>
              <Input
                id="rfi-assigned-to"
                placeholder="Contact name"
                value={formData.assigned_to ?? ''}
                onChange={(e) => handleChange('assigned_to', e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="rfi-assigned-email">Assigned Email</Label>
              <Input
                id="rfi-assigned-email"
                type="email"
                placeholder="contact@firm.com"
                value={formData.assigned_to_email ?? ''}
                onChange={(e) => handleChange('assigned_to_email', e.target.value)}
                aria-invalid={Boolean(emailError) || undefined}
                aria-describedby={emailError ? 'rfi-assigned-email-error' : undefined}
              />
              {emailError && (
                <p id="rfi-assigned-email-error" className="text-xs text-destructive">
                  {emailError}
                </p>
              )}
            </div>
          </div>

          {/* Due date */}
          <div className="space-y-1">
            <Label htmlFor="rfi-date-due">Date Due</Label>
            <Input
              id="rfi-date-due"
              type="date"
              value={formData.date_due ?? ''}
              onChange={(e) => handleChange('date_due', e.target.value)}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSave}>
            {isSaving ? 'Saving...' : isEditing ? 'Save RFI' : 'Create RFI'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
